import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { format, parseISO } from "date-fns";
import { ArrowLeft, Calendar, Clock, Mail, Phone, User, Trash2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminAppointmentsPage = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check auth
    const adminAuth = sessionStorage.getItem("adminAuth");
    if (adminAuth !== "true") {
      navigate("/admin");
      return;
    }
    fetchAppointments();
  }, [navigate]);

  const fetchAppointments = async () => {
    try {
      const response = await axios.get(`${API}/appointments`);
      setAppointments(response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      ));
    } catch (error) {
      console.error("Error fetching appointments:", error);
    } finally {
      setLoading(false);
    }
  };

  const openDeleteDialog = (appointment) => {
    setSelectedAppointment(appointment);
    setDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!selectedAppointment) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/appointments/${selectedAppointment.id}`);
      toast.success("Appointment deleted successfully");
      setDeleteDialog(false);
      fetchAppointments();
    } catch (error) {
      toast.error("Failed to delete appointment");
    } finally {
      setDeleting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed": return "text-green-500 bg-green-500/10";
      case "cancelled": return "text-red-500 bg-red-500/10";
      case "pending": return "text-yellow-500 bg-yellow-500/10";
      default: return "text-muted-foreground bg-white/5";
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-8 border-b border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <Button
            variant="ghost"
            onClick={() => navigate("/admin")}
            className="mb-4"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back to Admin
          </Button>
          <h1 className="font-serif text-3xl tracking-tight">All Appointments</h1>
          <p className="text-muted-foreground mt-2">{appointments.length} total appointments</p>
        </div>
      </section>

      {/* Appointments List */}
      <section className="py-8">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          {loading ? (
            <p className="text-muted-foreground">Loading...</p>
          ) : appointments.length === 0 ? (
            <p className="text-muted-foreground">No appointments found</p>
          ) : (
            <div className="space-y-4">
              {appointments.map((apt, index) => (
                <motion.div
                  key={apt.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-card border border-white/10 p-6"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <h3 className="font-serif text-lg">{apt.service_name || apt.service_type}</h3>
                        <span className={`text-xs px-2 py-1 rounded ${getStatusColor(apt.status)}`}>
                          {apt.status}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <User size={14} />
                          <span>{apt.client_name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Mail size={14} />
                          <span>{apt.client_email}</span>
                        </div>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Phone size={14} />
                          <span>{apt.client_phone}</span>
                        </div>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Calendar size={14} />
                          <span>
                            {apt.appointment_date && format(parseISO(apt.appointment_date), "MMM d, yyyy")}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 text-sm">
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Clock size={14} />
                          {apt.appointment_time}
                        </span>
                        {apt.estimated_arrival_date && (
                          <span className="text-primary text-xs">
                            Est. Arrival: {format(parseISO(apt.estimated_arrival_date), "MMM d, yyyy")}
                          </span>
                        )}
                      </div>

                      {apt.notes && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Notes: {apt.notes}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openDeleteDialog(apt)}
                        className="text-red-400 border-red-500/30 hover:bg-red-500 hover:text-white"
                      >
                        <Trash2 size={14} className="mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Delete Appointment</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Are you sure you want to delete this appointment? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {selectedAppointment && (
            <div className="py-4 border-y border-white/10 my-4">
              <p className="font-medium">{selectedAppointment.client_name}</p>
              <p className="text-sm text-muted-foreground">
                {selectedAppointment.service_name || selectedAppointment.service_type}
              </p>
              <p className="text-sm text-muted-foreground">
                {selectedAppointment.appointment_date && format(parseISO(selectedAppointment.appointment_date), "MMMM d, yyyy")} at {selectedAppointment.appointment_time}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialog(false)}
              className="border-white/20"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {deleting ? "Deleting..." : "Delete Appointment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAppointmentsPage;
